// @ts-strict-ignore
import React, { useEffect, useState } from 'react';

import { send } from 'loot-core/src/platform/client/fetch';

import { authorizeBank as quilttAuthorizeBank } from '../../quiltt';
import { authorizeBank as gocardlessAuthorizeBank } from '../../gocardless';
import { useActions } from '../../hooks/useActions';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useGoCardlessStatus } from '../../hooks/useGoCardlessStatus';
import { useSimpleFinStatus } from '../../hooks/useSimpleFinStatus';
import { type SyncServerStatus } from '../../hooks/useSyncServerStatus';
import { theme } from '../../style';
import { Button, ButtonWithLoading } from '../common/Button';
import { Link } from '../common/Link';
import { Modal } from '../common/Modal';
import { Paragraph } from '../common/Paragraph';
import { Text } from '../common/Text';
import { View } from '../common/View';
import { type CommonModalProps } from '../Modals';
import { useAuth } from '../../hooks/useAuth';
import { useDispatch } from 'react-redux';
import { closeModal, pushModal } from 'loot-core/client/actions';
import { useNavigate } from '../../hooks/useNavigate';

type CreateAccountProps = {
  modalProps: CommonModalProps;
  syncServerStatus: SyncServerStatus;
  upgradingAccountId?: string;
};

export function CreateAccountModal({
  modalProps,
  syncServerStatus,
  upgradingAccountId,
}: CreateAccountProps) {
  const actions = useActions();
  const { billingStatus } = useAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // const [isGoCardlessSetupComplete, setIsGoCardlessSetupComplete] =
  //   useState(null);
  // const [isSimpleFinSetupComplete, setIsSimpleFinSetupComplete] =
  //   useState(null);

  // skip the intermediary step when coming from Account => Link account
  useEffect(() => {
    if (upgradingAccountId) {
      dispatch(closeModal());
      onConnectQuiltt();
    }
  }, []);

  // const onConnectGoCardless = () => {
  //   if (!isGoCardlessSetupComplete) {
  //     onGoCardlessInit();
  //     return;
  //   }
  //
  //   if (upgradingAccountId == null) {
  //     gocardlessAuthorizeBank(actions.pushModal);
  //   } else {
  //     gocardlessAuthorizeBank(actions.pushModal, {
  //       upgradingAccountId,
  //     });
  //   }
  // };
  //
  const onConnectQuiltt = async () => {
    if (!billingStatus.isActive) {
      dispatch(pushModal('confirm-generic', {
        title: 'Upgrade your account',
        text: 'Connecting a bank account requires a subscription plan. Please subscribe to a plan to activate this feature. (30-day free trial available).',
        confirmBtnText: 'Go to billing',
        onConfirm: () => {
          navigate('/billing');
        }
      }));
      return;
    } else if (!billingStatus.canAddNewInstitution) {
      dispatch(pushModal('confirm-generic', {
        title: 'Upgrade your account',
        text: 'You have reached the maximum number of connected financial institutions allowed for your current subscription plan. Please upgrade to the next tier in order to connect to more institutions.',
        cancelBtnText: 'Cancel',
        confirmBtnText: 'Go to billing',
        onConfirm: () => {
          navigate('/billing');
        }
      }));
      return;
    }

    if (upgradingAccountId == null) {
      quilttAuthorizeBank(actions.pushModal);
    } else {
      quilttAuthorizeBank(actions.pushModal, {
        upgradingAccountId,
      });
    }
  };

  // const onConnectSimpleFin = async () => {
  //   if (!isSimpleFinSetupComplete) {
  //     onSimpleFinInit();
  //     return;
  //   }
  //
  //   if (loadingSimpleFinAccounts) {
  //     return;
  //   }
  //
  //   setLoadingSimpleFinAccounts(true);
  //
  //   const results = await send('simplefin-accounts');
  //
  //   const newAccounts = [];
  //
  //   type NormalizedAccount = {
  //     account_id: string;
  //     name: string;
  //     institution: string;
  //     orgDomain: string;
  //     balance: number;
  //   };
  //
  //   for (const oldAccount of results.accounts) {
  //     const newAccount: NormalizedAccount = {
  //       account_id: oldAccount.id,
  //       name: oldAccount.name,
  //       institution: oldAccount.org.name,
  //       orgDomain: oldAccount.org.domain,
  //       balance: oldAccount.balance,
  //     };
  //
  //     newAccounts.push(newAccount);
  //   }
  //
  //   actions.pushModal('select-linked-accounts', {
  //     accounts: newAccounts,
  //     syncSource: 'simpleFin',
  //   });
  //
  //   setLoadingSimpleFinAccounts(false);
  // };

  // const onGoCardlessInit = () => {
  //   actions.pushModal('gocardless-init', {
  //     onSuccess: () => setIsGoCardlessSetupComplete(true),
  //   });
  // };
  //
  // const onSimpleFinInit = () => {
  //   actions.pushModal('simplefin-init', {
  //     onSuccess: () => setIsSimpleFinSetupComplete(true),
  //   });
  // };

  const onCreateLocalAccount = () => {
    actions.pushModal('add-local-account');
  };

  // const { configuredGoCardless } = useGoCardlessStatus();
  // useEffect(() => {
  //   setIsGoCardlessSetupComplete(configuredGoCardless);
  // }, [configuredGoCardless]);
  //
  // const { configuredSimpleFin } = useSimpleFinStatus();
  // useEffect(() => {
  //   setIsSimpleFinSetupComplete(configuredSimpleFin);
  // }, [configuredSimpleFin]);

  let title = 'Add Account';
  // const [loadingSimpleFinAccounts, setLoadingSimpleFinAccounts] =
  //   useState(false);

  if (upgradingAccountId != null) {
    title = 'Link Account';
  }

  const simpleFinSyncFeatureFlag = useFeatureFlag('simpleFinSync');

  return (
    <Modal title={title} {...modalProps}>
      {() => (
        <View style={{ maxWidth: 500, gap: 30, color: theme.pageText }}>
          <View style={{ gap: 10 }}>
            <ButtonWithLoading
              type={'primary'}
              disabled={syncServerStatus !== 'online'}
              style={{
                padding: '10px 0',
                fontSize: 15,
                fontWeight: 600,
                flex: 1,
              }}
              onClick={onConnectQuiltt}
            >
              Connect Your Financial Institution
            </ButtonWithLoading>
          </View>
          {upgradingAccountId == null && (
            <View style={{ gap: 10 }}>
              <Button
                style={{
                  padding: '10px 0',
                  fontSize: 15,
                  fontWeight: 600,
                }}
                onClick={onCreateLocalAccount}
              >
                Create local account
              </Button>
              <View style={{ lineHeight: '1.4em', fontSize: 15 }}>
                <Text>
                  <strong>Create a local account</strong> if you want to add
                  transactions manually. You can also{' '}
                  <Link
                    variant="external"
                    to="https://chatwoot.goodsteward.io/hc/gs-kb/articles/1748442663-import"
                    linkColor="muted"
                  >
                    import CSV/QIF/OFX/QFX files into a local account
                  </Link>
                  .
                </Text>
              </View>
            </View>
          )}
        </View>
      )}
    </Modal>
  );
}
